import React, { useState, useMemo, useContext, useCallback } from 'react';

import { isBookingEvent } from '@libs/EventTypes';
import { getMoment } from '@libs/NwMoment';

import { useEventsSearchContext } from '@contexts/EventsSearchContext';
import { ListContext } from '@contexts/ListContext';

import { getContactName } from '@hooks/API/Contact/UseContactsList';
import useCountryName from '@hooks/CachedLists/UseCountryName';

import EventDrawer from '@components/EventDrawer/EventDrawer';
import BookingEvent from '@components/EventsBoxes/BookingEvent';
import NonBookingEvent from '@components/EventsBoxes/NonBookingEvent';
import EmptyState from '@components/Gui/EmptyState';
import { Loading } from '@components/Gui/Loading';
import { NwCard } from '@components/Gui/NwCard/NwCard';

import EventsSearchHeader from '@containers/Booking/SearchEvents/EventsSearchHeader';
import EventsSearchHeaderDrawer from '@containers/Booking/SearchEvents/EventsSearchHeaderDrawer';

const EventsSearchList = ({ events, loading, modelID, onFetchEvents }) => {
    const [selectedEvent, setSelectedEvent] = useState();

    const { cachedList } = useContext(ListContext);
    const { sort, nameFilter, isDrawerMode } = useEventsSearchContext();

    // const getServiceName = useCallback(
    //     (serviceID) => {
    //         return getContactName(serviceID);
    //     },
    //     [cachedList.contacts]
    // );

    // const getMeetingContactName = useCallback((contactID) => {
    //     return getContactName(contactID);
    // }, [cachedList.contacts]);

    // const getCountryName = useCallback((country) => {
    //     return useCountryName(country);
    // }, [cachedList.countries]);

    const getProjectCategory = useCallback(
        (categoryID) => {
            const category = cachedList.projectCategories.items.find((category) => category.ID === categoryID);
            if (category) {
                return category.Name;
            }
            return '';
        },
        [cachedList.projectCategories]
    );

    const handleSelectEvent = (eventdata) => {
        const groupableEvents = ['followup', 'travel', 'accommodation'];
        let eventToSet = { ...eventdata };
        if (groupableEvents.includes(eventdata.type.toLowerCase())) {
            let idtoset = eventdata.GroupedEventIDs[0];
            if (modelID) {
                const selectedModel = eventdata.Models.find((model) => model.ModelID === modelID);
                if (selectedModel) {
                    switch (eventdata.type) {
                        case 'travel':
                            idtoset = selectedModel.TravelID;
                            break;
                        case 'accommodation':
                            idtoset = selectedModel.AccommodationID;
                            break;
                        case 'followup':
                            idtoset = selectedModel.FollowUpID;
                            break;
                        default:
                            break;
                    }
                }
            }
            eventToSet = { ...eventdata, ID: idtoset };
        }
        setSelectedEvent(eventToSet);
    };

    const EventsWithProjectCategoryAndNames = useMemo(() => {
        // Ensure events is an array
        const eventsArray = Array.isArray(events) ? events : [];

        const eventsWithProjectCategoryAndNames = eventsArray.map((event) => {
            if (event.Project && event.Project.ProjectCategoryID) {
                event.Project.ProjectCategoryName = getProjectCategory(event.Project.ProjectCategoryID);
            }
            if (event.ServiceID) {
                event.ServiceName = getContactName(event.ServiceID);
            }
            if (event.ContactID) {
                event.MeetingContactName = getContactName(event.ContactID);
            }
            if (event.Country) {
                event.CountryName = useCountryName(event.Country);
            }
            return event;
        });
        return eventsWithProjectCategoryAndNames;
    }, [events]);

    const EventsGroupedAndSorted = useMemo(() => {
        let sortedEvents = EventsWithProjectCategoryAndNames.map((event) => {
            switch (event.type.toLowerCase()) {
                case 'travel':
                    return {
                        ...event,
                        StartDate: event.StartDateTime,
                    };
                case 'accommodation':
                    return {
                        ...event,
                        ID: event.Models && event.Models[0].AccommodationID,
                        StartDate: event.FromDate,
                    };
                case 'todo':
                    return {
                        ...event,
                        StartDate: event.Date,
                    };
                case 'casting':
                case 'job':
                case 'ext':
                    return {
                        ...event,
                        Title: event.Name,
                        StartDate: event.FirstDate,
                    };
                case 'extjob':
                case 'followup':
                case 'meeting':
                default:
                    return event;
            }
        });

        sortedEvents.sort((e1, e2) => {
            if (sort === 'time-up') {
                if (!e1.StartDate) return -1;
                if (!e2.StartDate) return 1;
                return getMoment(e1.StartDate) > getMoment(e2.StartDate) ? 1 : -1;
            } else if (sort === 'time-down') {
                if (!e1.StartDate) return 1;
                if (!e2.StartDate) return -1;
                return getMoment(e1.StartDate) < getMoment(e2.StartDate) ? 1 : -1;
            } else {
                return e1.type > e2.type ? 1 : -1;
            }
        });

        let groupedEvents = [];
        for (const event of sortedEvents) {
            if (!event.GroupGuid) {
                groupedEvents.push(event);
            } else {
                const eventIndex = groupedEvents.findIndex((item) => item.GroupGuid === event.GroupGuid);
                if (eventIndex > -1) {
                    groupedEvents[eventIndex].RelatedEvents.push(event);
                } else {
                    groupedEvents.push({
                        ...event,
                        RelatedEvents: [],
                    });
                }
            }
        }
        return groupedEvents;
    }, [EventsWithProjectCategoryAndNames, sort]);

    const EventsFiltered = useMemo(() => {
        if (!nameFilter) {
            if (EventsGroupedAndSorted) {
                return EventsGroupedAndSorted;
            }
            return [];
        }
        if (!EventsGroupedAndSorted) {
            return [];
        }
        return EventsGroupedAndSorted.filter((event) => {
            if (event.Title && event.Title.toLowerCase().includes(nameFilter.toLowerCase())) {
                return true;
            }
            if (event.Description && event.Description.toLowerCase().includes(nameFilter.toLowerCase())) {
                return true;
            }
            if (event.Project) {
                if (event.Project.Name && event.Project.Name.toLowerCase().includes(nameFilter.toLowerCase())) {
                    return true;
                }
                if (event.Project.Product && event.Project.Product.toLowerCase().includes(nameFilter.toLowerCase())) {
                    return true;
                }
                if (
                    event.Project.ProjectCategoryName &&
                    event.Project.ProjectCategoryName.toLowerCase().includes(nameFilter.toLowerCase())
                ) {
                    return true;
                }
            }
            if (event.type.toLowerCase() === 'travel') {
                if (event.StartLocation && event.StartLocation.toLowerCase().includes(nameFilter.toLowerCase())) {
                    return true;
                }
                if (event.ArrivalLocation && event.ArrivalLocation.toLowerCase().includes(nameFilter.toLowerCase())) {
                    return true;
                }
                // THESE PROPERTIES ARE NOT INCLUDED IN THE DTO (TICKETID, BOOKINGREFERENCE) OR NOT SHOWN (TRANSPORT)
                // if (event.Transport && event.Transport.toLowerCase().includes(nameFilter.toLowerCase())) {
                //     return true;
                // }
                // if (event.TicketID && event.TicketID.toLowerCase().includes(nameFilter.toLowerCase())) {
                //     return true;
                // }
                // if (event.BookingReference && event.BookingReference.toLowerCase().includes(nameFilter.toLowerCase())) {
                //     return true;
                // }
            }
            if (event.type.toLowerCase() === 'accommodation' || event.type.toLowerCase() === 'roomlodging') {
                if (event.Street && event.Street.toLowerCase().includes(nameFilter.toLowerCase())) {
                    return true;
                }
                if (event.City && event.City.toLowerCase().includes(nameFilter.toLowerCase())) {
                    return true;
                }
                if (event.Zip && event.Zip.toLowerCase().includes(nameFilter.toLowerCase())) {
                    return true;
                }
                if (event.CountryName && event.CountryName.toLowerCase().includes(nameFilter.toLowerCase())) {
                    return true;
                }
                if (event.State && event.State.toLowerCase().includes(nameFilter.toLowerCase())) {
                    return true;
                }
            }
            if (event.type.toLowerCase() === 'extjob') {
                if (event.ClientName && event.ClientName.toLowerCase().includes(nameFilter.toLowerCase())) {
                    return true;
                }
                if (event.ProductName && event.ProductName.toLowerCase().includes(nameFilter.toLowerCase())) {
                    return true;
                }
                if (event.Location && event.Location.toLowerCase().includes(nameFilter.toLowerCase())) {
                    return true;
                }
                if (event.TimeSheet && event.TimeSheet.toLowerCase().includes(nameFilter.toLowerCase())) {
                    return true;
                }
            }
            if (event.type.toLowerCase() === 'meeting') {
                if (
                    event.MeetingContactName &&
                    event.MeetingContactName.toLowerCase().includes(nameFilter.toLowerCase())
                ) {
                    return true;
                }
                if (event.Street && event.Street.toLowerCase().includes(nameFilter.toLowerCase())) {
                    return true;
                }
                if (event.City && event.City.toLowerCase().includes(nameFilter.toLowerCase())) {
                    return true;
                }
                if (event.Zip && event.Zip.toLowerCase().includes(nameFilter.toLowerCase())) {
                    return true;
                }
                if (event.CountryName && event.CountryName.toLowerCase().includes(nameFilter.toLowerCase())) {
                    return true;
                }
                if (event.State && event.State.toLowerCase().includes(nameFilter.toLowerCase())) {
                    return true;
                }
            }
            if (event.ServiceName && event.ServiceName.toLowerCase().includes(nameFilter.toLowerCase())) {
                return true;
            }
            return false;
        });
    }, [EventsGroupedAndSorted, nameFilter]);

    const getEventType = (type) => {
        // if (type === 'extjob') {
        //     return 'extjob';
        // }
        return type;
    };

    return (
        <>
            {isDrawerMode ? (
                <EventsSearchHeaderDrawer events={EventsFiltered} loading={loading} eventsGrouped={false} />
            ) : (
                <EventsSearchHeader events={EventsFiltered} loading={loading} eventsGrouped={false} />
            )}
            {loading ? (
                <Loading />
            ) : (
                <NwCard>
                    {EventsFiltered && EventsFiltered.length > 0 ? (
                        <>
                            {EventsFiltered.map((event, index) => {
                                if (isBookingEvent(event.type)) {
                                    return (
                                        <BookingEvent
                                            key={`${event.type}-${index}`}
                                            placement="bottom"
                                            loadEvents={onFetchEvents}
                                            event={event}
                                            expanded={true}
                                            onSelectEvent={handleSelectEvent}
                                            page="event-search"
                                        />
                                    );
                                } else {
                                    return (
                                        <NonBookingEvent
                                            key={`${event.type}-${index}`}
                                            event={event}
                                            onSelectEvent={handleSelectEvent}
                                            page="event-search"
                                        />
                                    );
                                }
                            })}
                        </>
                    ) : (
                        <EmptyState message="no events" />
                    )}
                </NwCard>
            )}
            {selectedEvent && (
                <EventDrawer
                    eventType={getEventType(selectedEvent.type)}
                    eventID={selectedEvent.ID}
                    eventObject={selectedEvent}
                    onClose={() => setSelectedEvent(null)}
                    afterUpdate={onFetchEvents}
                    afterRemove={onFetchEvents}
                />
            )}
        </>
    );
};

export default EventsSearchList;
